import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { CheckCircle2, ShoppingBag, Layers, Clock } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import api from "@/lib/api";

function PickDialog({ bundle, decks, triggerClassName }) {
  const { add } = useCart();
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState([]);
  const toggle = (id) => setPicked((p) => p.includes(id) ? p.filter((x) => x !== id) : p.length < bundle.pick ? [...p, id] : p);
  const confirm = () => {
    if (picked.length !== bundle.pick) { toast.error(`Please choose exactly ${bundle.pick} decks.`); return; }
    const names = decks.filter((d) => picked.includes(d.id)).map((d) => d.title).join(", ");
    add({ item_id: bundle.id, name: bundle.title, price: bundle.price, kind: "bundle", workshop_ids: picked, detail: names });
    setPicked([]); setOpen(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setPicked([]); }}>
      <DialogTrigger asChild>
        <Button className={triggerClassName} data-testid={`deck-bundle-pick-${bundle.id}`}>
          <ShoppingBag className="w-4 h-4" /> Choose {bundle.pick} · ${bundle.price}
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="deck-bundle-dialog">
        <DialogHeader><DialogTitle className="font-display">{bundle.title}</DialogTitle></DialogHeader>
        <DialogDescription className="-mt-2 text-sm text-slate-600">
          Pick {bundle.pick} decks ({picked.length}/{bundle.pick} selected) — ${bundle.price} for all three instead of ${bundle.compare_price}.
        </DialogDescription>
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1 mt-1">
          {decks.map((d) => {
            const capped = !picked.includes(d.id) && picked.length >= bundle.pick;
            return (
              <label key={d.id} className={`flex items-start gap-3 rounded-lg border p-3 transition-colors ${capped ? "border-slate-100 opacity-45 cursor-not-allowed" : "border-slate-200 cursor-pointer hover:border-teal-400"}`}>
                <Checkbox checked={picked.includes(d.id)} disabled={capped} onCheckedChange={() => toggle(d.id)} data-testid={`deck-pick-${d.id}`} />
                <span className="text-sm text-slate-800 leading-snug">{d.title.replace(" — Workshop Slide Deck", "")}</span>
              </label>
            );
          })}
        </div>
        <Button onClick={confirm} className="w-full bg-teal-600 hover:bg-teal-700 rounded-full mt-2" data-testid="deck-bundle-confirm">
          Add bundle to basket · ${bundle.price}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export default function DeckBundles() {
  const { add } = useCart();
  const [bundles, setBundles] = useState([]);
  const [decks, setDecks] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/bundles"), api.get("/resources")])
      .then(([b, r]) => {
        setBundles(b.data.filter((x) => x.kind === "deck"));
        setDecks(r.data.filter((x) => x.category === "Workshop Decks"));
      }).catch(() => {});
  }, []);

  if (!bundles.length) return null;

  return (
    <div className="mb-10" data-testid="deck-bundles">
      <div className="grid md:grid-cols-2 gap-6">
        {bundles.map((b) => (
          <Card key={b.id} className={`p-7 ${b.available ? "border-teal-200 bg-teal-50/40" : "border-slate-200 bg-slate-50"}`} data-testid={`deck-bundle-${b.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <Layers className="w-5 h-5 text-teal-600 mb-2" />
                <h3 className="font-display text-lg font-bold text-slate-900">{b.title}</h3>
                <p className="text-sm text-slate-600 mt-1">{b.description}</p>
              </div>
              <div className="text-right shrink-0">
                <div className={`font-display font-bold ${b.available ? "text-2xl text-slate-900" : "text-lg text-slate-400"}`}>${b.price}</div>
                {b.compare_price && b.available && <div className="text-xs text-slate-400 line-through">${b.compare_price}</div>}
                {!b.available && <div className="text-[10px] uppercase tracking-wide text-slate-400 mt-0.5">when released</div>}
              </div>
            </div>
            <ul className="mt-4 space-y-1.5">
              {b.features?.map((f) => (
                <li key={f} className="flex gap-2 text-sm text-slate-600"><CheckCircle2 className="w-4 h-4 text-teal-600 shrink-0 mt-0.5" /> {f}</li>
              ))}
            </ul>
            <div className="mt-5">
              {!b.available ? (
                <div data-testid={`deck-bundle-soon-${b.id}`}>
                  <Badge variant="secondary" className="mb-2"><Clock className="w-3 h-3 mr-1" /> Coming soon</Badge>
                  <p className="text-xs text-slate-500">{b.decks_available} of {b.decks_total} decks released — this bundle opens once the full series is available.</p>
                </div>
              ) : b.pick >= b.decks_total ? (
                <Button className="w-full bg-teal-600 hover:bg-teal-700 rounded-full"
                  onClick={() => add({ item_id: b.id, name: b.title, price: b.price, kind: "bundle", detail: `All ${b.decks_total} decks` })}
                  data-testid={`deck-bundle-buy-${b.id}`}>
                  <ShoppingBag className="w-4 h-4" /> Add to basket · ${b.price}
                </Button>
              ) : (
                <PickDialog bundle={b} decks={decks} triggerClassName="w-full bg-teal-600 hover:bg-teal-700 rounded-full" />
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
